import type { RangePart } from "./types";

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidDate(value: string): boolean {
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const [, y, m, d] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return date.toISOString().slice(0, 10) === value;
}

export function buildHourRange(localDate: string, startHour: number, endHour: number): RangePart[] {
  if (!isValidDate(localDate)) throw new Error("INVALID_DATE");
  if (!Number.isInteger(startHour) || startHour < 0 || startHour > 23) throw new Error("INVALID_START_HOUR");
  if (!Number.isInteger(endHour) || endHour < 0 || endHour > 23) throw new Error("INVALID_END_HOUR");
  if (endHour < startHour) throw new Error("RANGE_END_BEFORE_START");
  return Array.from({ length: endHour - startHour + 1 }, (_, index) => ({
    localDate,
    slotHour: startHour + index,
    durationMinutes: 60,
  }));
}

export function buildMinuteInterval(
  localDate: string,
  startHour: number,
  startMinute: number,
  endHour: number,
  endMinute: number,
): RangePart[] {
  if (!isValidDate(localDate)) throw new Error("INVALID_DATE");
  if (!Number.isInteger(startHour) || startHour < 0 || startHour > 23) throw new Error("INVALID_START_HOUR");
  if (!Number.isInteger(startMinute) || startMinute < 0 || startMinute > 59) throw new Error("INVALID_START_MINUTE");
  if (!Number.isInteger(endHour) || endHour < 0 || endHour > 23) throw new Error("INVALID_END_HOUR");
  if (!Number.isInteger(endMinute) || endMinute < 1 || endMinute > 60) throw new Error("INVALID_END_MINUTE");

  const start = startHour * 60 + startMinute;
  const end = endHour * 60 + endMinute;
  if (end <= start) throw new Error("INTERVAL_END_NOT_AFTER_START");

  const parts: RangePart[] = [];
  let cursor = start;
  while (cursor < end) {
    const slotHour = Math.floor(cursor / 60);
    const partEnd = Math.min(end, (slotHour + 1) * 60);
    parts.push({ localDate, slotHour, durationMinutes: partEnd - cursor });
    cursor = partEnd;
  }
  return parts;
}

export function zonedNow(timeZone: string, now = new Date()): { date: string; hour: number; minute: number } {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(formatter.formatToParts(now).map((part) => [part.type, part.value]));
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour),
    minute: Number(parts.minute),
  };
}

export function slotCapacityForDate(
  localDate: string,
  timeZone: string,
  now = new Date(),
): number[] {
  const current = zonedNow(timeZone, now);
  if (localDate < current.date) return Array(24).fill(60);
  if (localDate > current.date) return Array(24).fill(0);
  return Array.from({ length: 24 }, (_, hour) => {
    if (hour < current.hour) return 60;
    if (hour > current.hour) return 0;
    return current.minute;
  });
}

export function slotEditCapacityForDate(
  localDate: string,
  timeZone: string,
  now = new Date(),
): number[] {
  const current = zonedNow(timeZone, now);
  if (localDate < current.date) return Array(24).fill(60);
  if (localDate > current.date) return Array(24).fill(0);
  return Array.from({ length: 24 }, (_, hour) => hour <= current.hour ? 60 : 0);
}

export function addDays(date: string, days: number): string {
  if (!isValidDate(date)) throw new Error("INVALID_DATE");
  const result = new Date(`${date}T00:00:00Z`);
  result.setUTCDate(result.getUTCDate() + days);
  return result.toISOString().slice(0, 10);
}

export function enumerateDates(from: string, to: string, limit = 367): string[] {
  if (!isValidDate(from) || !isValidDate(to) || from > to) throw new Error("INVALID_DATE_RANGE");
  const result: string[] = [];
  let cursor = from;
  while (cursor <= to) {
    result.push(cursor);
    if (result.length > limit) throw new Error("DATE_RANGE_TOO_LONG");
    cursor = addDays(cursor, 1);
  }
  return result;
}

export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}分钟`;
  if (!rest) return `${hours}小时`;
  return `${hours}小时${rest}分钟`;
}
